import React, { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { Flex, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAppContext } from "~/contexts/appContext";
import { UserRoles } from "~/types/user";
import { isTreasuryPlatformManager } from "~/utils/userRoles";
import { CategoryCard } from "./CategoryCard";
import {
  GLOBAL_TOOL_CATEGORIES,
  PROVIDER_TOOL_CATEGORIES,
  FIDES_TOOL_CATEGORY,
} from "./constants";
import * as styles from "./styles_vanilla.css";
import { ToolCategory } from "./types";

const hasVisibleTools = (category: ToolCategory, roles: UserRoles): boolean => {
  return category.tools.some((tool) =>
    tool.checkPermission ? tool.checkPermission(roles) : isTreasuryPlatformManager(roles),
  );
};

const ConfigurationPage: React.FC = () => {
  const history = useHistory();
  const { user } = useAppContext();
  const isFidesConfigsEnabled = useFeatureFlag("fidesConfigs", false);

  const visibleGlobalCategories = useMemo(() => {
    return GLOBAL_TOOL_CATEGORIES.filter((category) => hasVisibleTools(category, user.roles));
  }, [user.roles]);

  const visibleProviderCategories = useMemo(() => {
    const categories = isFidesConfigsEnabled
      ? [FIDES_TOOL_CATEGORY, ...PROVIDER_TOOL_CATEGORIES]
      : PROVIDER_TOOL_CATEGORIES;
    return categories.filter((category) => hasVisibleTools(category, user.roles));
  }, [isFidesConfigsEnabled, user.roles]);

  const handleNavigation = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history],
  );

  return (
    <Flex className={styles.container} flexDirection="column" gap={Sizes.XL}>
      <Flex flexDirection="column" gap={Sizes.XS}>
        <Text variant="regular-xxxl">Configuration tools</Text>
        <Text variant="regular-md" className={styles.subtitle}>
          Troubleshoot payments, manage configurations, and handle exceptions.
        </Text>
      </Flex>

      {visibleGlobalCategories.length > 0 && (
        <Flex flexDirection="column" gap={Sizes.MD}>
          <Text variant="strong-xl">Global tools</Text>
          <Flex flexDirection="column" gap={Sizes.MD}>
            {visibleGlobalCategories.map((category) => (
              <CategoryCard
                key={category.title}
                category={category}
                onToolClick={handleNavigation}
              />
            ))}
          </Flex>
        </Flex>
      )}

      {visibleProviderCategories.length > 0 && (
        <Flex flexDirection="column" gap={Sizes.MD}>
          <Text variant="strong-xl">Provider specific tools</Text>
          <Flex flexDirection="column" gap={Sizes.MD}>
            {visibleProviderCategories.map((category) => (
              <CategoryCard
                key={category.title}
                category={category}
                onToolClick={handleNavigation}
              />
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default ConfigurationPage;
